import React, { ChangeEvent, FocusEvent, useState } from 'react';
import { FrontendFilterValue } from '../../types/filters.type';
import useFilterStore from '../streetview/filterStore';
import { isValidInputKey, validateMinAndMax } from '../../utils/helpers';
import FilterDescription from '../streetview/filterDescription';

export default function InputNumberToNumber({
  title,
  label,
  labelId,
  filterValues,
  description,
  featureFlag,
}: Readonly<{
  title: string,
  label: string,
  labelId: string,
  filterValues: FrontendFilterValue[],
  description?: string | null,
  featureFlag?: string | null,
}>) {
  const defaultMinimum = filterValues[0].id;
  const defaultMaximum = filterValues[filterValues.length - 1].id;

  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const setSelectedFilters = useFilterStore((state) => state.setSelectedFilters);

  const minimumState = selectedFilters[title]?.[labelId]?.selectedValues ? selectedFilters[title][labelId].selectedValues[0].id : '';
  const maximumState = selectedFilters[title]?.[labelId]?.selectedValues ? selectedFilters[title][labelId].selectedValues[1].id : '';

  const [minValue, setMinValue] = useState(minimumState);
  const [maxValue, setMaxValue] = useState(maximumState);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleMinimumInputChange = (value: string) => {
    const targetValue = value.replace(/[^0-9.-]+/g, '');
    const numericValue = Number(targetValue);
    if (numericValue > Number(defaultMaximum)) {
      setErrorMessage('Minimum value must be less than maximum range value.');
    } else {
      setErrorMessage(null);
    }
    setMinValue(targetValue);
  };

  const validateMinimumInputBlur = (value: string) => {
    const { error, isValid, filters } = validateMinAndMax(value, maxValue, defaultMinimum, defaultMaximum);
    if (error) {
      setErrorMessage(error);
      return;
    }
    if (isValid) {
      setSelectedFilters(title, labelId, label, filters);
    }
  };

  const handleMaximumInputChange = (value: string) => {
    const targetValue = value.replace(/[^0-9.-]+/g, '');
    const numericValue = Number(targetValue);
    if (numericValue > Number(defaultMaximum)) {
      setErrorMessage('Maximum value must be less than maximum range value.');
    } else {
      setErrorMessage(null);
    }
    setMaxValue(targetValue);
  };

  const validateMaximumInputBlur = (value: string) => {
    const { error, isValid, filters } = validateMinAndMax(minValue, value, defaultMinimum, defaultMaximum);
    if (error) {
      setErrorMessage(error);
      return;
    }
    if (isValid) {
      setSelectedFilters(title, labelId, label, filters);
    }
  };

  const handleInvalidCharacters = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isValidInputKey(event)) {
      event.preventDefault();
      setErrorMessage('Must be a valid number.');
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  return (
    <fieldset
      id="slider-container"
      aria-describedby={`${labelId}-description`}
      className="h-fit flex space-y-2 flex-col justify-center my-2"
    >
      <legend className="input-label">{label}</legend>
      <FilterDescription ariaId={`${labelId}-description`} description={description} featureFlag={featureFlag} />
      {errorMessage && <span className="text-piptext-error text-xs font-thin">{errorMessage}</span>}
      <div className="min-h-10">
        <div className="space-x-2">
          <input
            id={`${labelId}-min-value`}
            className={`max-w-24 input-default ${
              errorMessage ? 'focus:input-error' : 'focus:input-focused'}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={`Minimum value: ${defaultMinimum}`}
            min={defaultMinimum}
            max={defaultMaximum}
            placeholder={defaultMinimum}
            value={minValue}
            onKeyDown={handleInvalidCharacters}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleMinimumInputChange(event.target.value)}
            onBlur={(event: FocusEvent<HTMLInputElement>) => validateMinimumInputBlur(event?.target.value)}
          />
          <span className="text-secondary text-sm">and</span>
          <input
            id={`${labelId}-max-value`}
            className={`max-w-24 input-default ${
              errorMessage ? 'focus:input-error' : 'focus:input-focused'}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label={`Maximum value: ${defaultMaximum}`}
            min={defaultMinimum}
            max={defaultMaximum}
            placeholder={defaultMaximum}
            value={maxValue}
            onKeyDown={handleInvalidCharacters}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleMaximumInputChange(event.target.value)}
            onBlur={(event: FocusEvent<HTMLInputElement>) => validateMaximumInputBlur(event?.target.value)}
          />
        </div>
      </div>
    </fieldset>
  );
}
